import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { formatInt } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/** One forum account (crm-integrations' forums_contact_accounts / forums_search). */
export interface ForumAccount {
  forum: string;
  forum_url: string;
  profile_url: string;
  username: string;
  contact_id?: number | null;
  match_status: string;
  trust_level: number | null;
  post_count: number;
  topics_entered: number;
  days_visited: number;
  groups: string[] | null;
  admin: boolean;
  moderator: boolean;
  suspended: boolean;
  silenced: boolean;
  created_mt?: string;
  last_seen_mt?: string;
  gone: boolean;
  synced_mt: string;
}

export const FORUM_NAMES: Record<string, string> = { creu: "CREU forum", mhu: "MHU forum", obu: "OBU forum" };

const TRUST: Record<number, string> = { 0: "new", 1: "basic", 2: "member", 3: "regular", 4: "leader" };

/** "Oct 25, 2026 3:04 PM MDT" (the server's Mountain Time string) → "Oct 25, 2026". */
export const dayOf = (mt: string | undefined) => (mt ? mt.split(" ").slice(0, 3).join(" ") : "");

/** The contact's accounts on the three Discourse forums; hidden when they have none (or the operation isn't there). */
export function ForumsCard({ contactId }: { contactId: number }) {
  const q = useQuery({
    queryKey: ["forum-accounts", contactId],
    queryFn: () => api<{ accounts: ForumAccount[] }>(`/api/v1/contacts/${contactId}/forum-accounts`),
    retry: false,
    staleTime: 60_000,
  });
  const accounts = q.data?.accounts ?? [];
  if (accounts.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Forums</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        {accounts.map((a) => (
          <div key={`${a.forum}-${a.username}`} className={a.gone ? "row-dim" : undefined}>
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
              <span className="max-w-full shrink-0 truncate">
                <span className="text-[var(--color-muted-foreground)]">{FORUM_NAMES[a.forum] ?? a.forum}: </span>
                <a
                  href={a.profile_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-medium text-[var(--color-primary)] hover:underline"
                  title={`Open @${a.username}'s profile on the ${FORUM_NAMES[a.forum] ?? a.forum} (new tab)`}
                >
                  @{a.username}
                  <ExternalLink className="ml-0.5 inline size-3 align-baseline" aria-hidden />
                </a>
              </span>
              <span className="flex gap-1">
                {(a.admin || a.moderator) && <Badge variant="default">{a.admin ? "admin" : "moderator"}</Badge>}
                {a.suspended && <Badge variant="destructive">suspended</Badge>}
                {a.silenced && <Badge variant="warning">silenced</Badge>}
                {a.gone && <Badge variant="muted">account gone</Badge>}
              </span>
            </div>
            <div className="text-xs text-[var(--color-muted-foreground)]">
              {a.last_seen_mt ? <span title={`Last seen ${a.last_seen_mt}`}>seen {dayOf(a.last_seen_mt)}</span> : "never seen"}
              {", "}
              <span className="num">{formatInt(a.post_count)}</span> {a.post_count === 1 ? "post" : "posts"}
              {a.trust_level != null && <span title={`Discourse trust level ${a.trust_level}`}>, {TRUST[a.trust_level] ?? `level ${a.trust_level}`}</span>}
              {a.created_mt && <span title={`Joined ${a.created_mt}`}>, joined {dayOf(a.created_mt)}</span>}
            </div>
            {(a.groups ?? []).length > 0 && <div className="truncate text-xs text-[var(--color-muted-foreground)]">Groups: {(a.groups ?? []).join(", ")}</div>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
